import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { tappedPodIdAtom } from '@/state/tappedPod/atoms';
import { selectedClientAtom } from '@/state/clients/atoms';
import { feedsInDisplayAtom } from '../atoms/feeds';
import {
  getLiveKitServiceInstance,
  RoomInstance,
} from '../services/LiveKitService';
import { createLiveKitFeedConfigsFromVideoTracks } from '../utils/trackConversion';
import { LiveKitFeedConfig } from '../types/feed';

export function usePodRoomConnection() {
  const tappedPodId = useAtomValue(tappedPodIdAtom);
  const selectedClient = useAtomValue(selectedClientAtom);
  const setFeedsInDisplay = useSetAtom(feedsInDisplayAtom);

  const liveKitService = getLiveKitServiceInstance();
  const podRoomsRef = useRef<Map<string, RoomInstance>>(new Map());

  const connectToPodRoom = useCallback(
    async (podId: string) => {
      if (
        !selectedClient?.client_id ||
        !selectedClient?.machine_id ||
        !selectedClient?.livekit_url
      ) {
        console.warn('No selected client or missing required client fields');
        return;
      }

      const roomName = `${selectedClient.machine_id}-${podId.toLowerCase()}`;
      const identity = `operator-${Date.now()}`;

      try {
        // Check if already connected
        let roomInstance = liveKitService.getRoomInstance(roomName, identity);

        if (!roomInstance) {
          // Create new room instance
          roomInstance = liveKitService.createRoomInstance({
            wsUrl: selectedClient.livekit_url,
            roomName,
            identity,
            clientId: selectedClient.client_id,
          });

          // Connect to the room
          await roomInstance.connect();

          // Store reference
          podRoomsRef.current.set(podId, roomInstance);

          console.log(`Connected to pod room: ${roomName}`);
        }

        // Get video tracks and convert to feed configs
        const videoTracks = roomInstance.getVideoTracks();
        const feedConfigs = createLiveKitFeedConfigsFromVideoTracks(
          videoTracks,
          roomName,
          podId,
        );

        // Add feeds to display (avoid duplicates)
        setFeedsInDisplay((prev) => {
          const existingFeedIds = new Set(prev.map((feed) => feed.id));
          const newFeeds = feedConfigs.filter(
            (feed) => !existingFeedIds.has(feed.id),
          );
          return [...prev, ...newFeeds];
        });

        return roomInstance;
      } catch (error) {
        console.error(`Failed to connect to pod room ${roomName}:`, error);
        throw error;
      }
    },
    [selectedClient, liveKitService, setFeedsInDisplay],
  );

  const disconnectFromPodRoom = useCallback(
    async (podId: string) => {
      if (!selectedClient?.machine_id) {
        return;
      }

      const roomName = `${selectedClient.machine_id}-${podId.toLowerCase()}`;
      const roomInstance = podRoomsRef.current.get(podId);

      if (roomInstance) {
        // Remove feeds from display
        setFeedsInDisplay((prev) =>
          prev.filter(
            (feed) =>
              !(
                feed.type === 'livekit' &&
                (feed as LiveKitFeedConfig).roomName === roomName
              ),
          ),
        );

        // Disconnect room
        await roomInstance.disconnect();
        liveKitService.destroyRoomInstance(
          roomName,
          roomInstance.getTracks()[0]?.participantIdentity || 'operator',
        );
        podRoomsRef.current.delete(podId);

        console.log(`Disconnected from pod room: ${roomName}`);
      }
    },
    [selectedClient, liveKitService, setFeedsInDisplay],
  );

  const togglePodConnection = useCallback(
    async (podId: string) => {
      const roomInstance = podRoomsRef.current.get(podId);

      if (roomInstance) {
        await disconnectFromPodRoom(podId);
      } else {
        await connectToPodRoom(podId);
      }
    },
    [connectToPodRoom, disconnectFromPodRoom],
  );

  // React to tapped pod changes
  useEffect(() => {
    if (tappedPodId && selectedClient) {
      togglePodConnection(tappedPodId).catch(console.error);
    }
  }, [tappedPodId, selectedClient, togglePodConnection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Disconnect all pod rooms on unmount
      podRoomsRef.current.forEach(async (roomInstance) => {
        await roomInstance.disconnect();
      });
      podRoomsRef.current.clear();
    };
  }, []);

  return {
    connectToPodRoom,
    disconnectFromPodRoom,
    togglePodConnection,
    connectedPods: Array.from(podRoomsRef.current.keys()),
  };
}
